import React from "react";
import * as Redux from "react-redux";
import { fetchBoundary } from "../../../store/boundary";
import { useConfig } from "../../../hooks/useConfig";
import {
  useAddressBoundary,
  useAllBoundaries,
  useSelectedPlacementBoundaries,
} from "../../../hooks/useBoundary";
import { BoundaryPolygon } from "./BoundaryPolygon";
import { Boundary } from "../../../types/Boundary";
import { InfoWindowF } from "@react-google-maps/api";
import { Text } from "@chakra-ui/react";
import { getBoundaryCenter } from "../../../services/geoJSON";

type MapPolygonsProps = {
  boundaries: Boundary[];
};
const MapPolygons: React.FC<MapPolygonsProps> = ({
  boundaries,
}: MapPolygonsProps) => {
  const [activeBoundary, setActiveBoundary] = React.useState<Boundary>();
  const [position, setPosition] = React.useState<google.maps.LatLng>();

  return (
    <>
      {boundaries
        .filter((boundary) => boundary)
        .map((boundary, index) => [
          <BoundaryPolygon
            key={`polygon-${index}`}
            boundary={boundary}
            onClick={(event) => {
              if (event.latLng) setPosition(event.latLng);
              setActiveBoundary(boundary);
            }}
          />,

          activeBoundary?.id === boundary.id && boundary.showMapLabel && (
            <InfoWindowF
              key={`polygon-info-${index}`}
              position={position ?? getBoundaryCenter(boundary)}
              onCloseClick={() => {
                setPosition(undefined);
                setActiveBoundary(undefined);
              }}
            >
              <Text marginRight={3}>{boundary.id}</Text>
            </InfoWindowF>
          ),
        ])}
    </>
  );
};

export const GeoJSONLayer: React.FC = () => {
  const dispatch = Redux.useDispatch();
  const config = useConfig();

  const allBoundaries = useAllBoundaries();
  const addressBoundaries = useAddressBoundary();
  const selectedPlacementBoundaries = useSelectedPlacementBoundaries();

  // warning!!! you must disable strict mode to debug this, or polygons drawn do not get removed from the map correctly
  const boundaries = React.useMemo((): Boundary[] => {
    const filteredAddressBoundaries: Boundary[] = addressBoundaries
      ? addressBoundaries.filter((boundary) => boundary.showAddressBoundary)
      : [];

    const filteredPlacementBoundaries: Boundary[] = selectedPlacementBoundaries
      ? selectedPlacementBoundaries.filter(
          (boundary) => boundary.showSelectedPlacementBoundary
        )
      : [];

    const filteredAllBoundaries: Boundary[] = allBoundaries
      ? allBoundaries.filter((boundary) => boundary.showAllBoundaries)
      : [];

    const dedupedBoundaries = new Map<string, Boundary>();
    // if an address boundary or filtered boundary is selected, only show those boundaries.
    if (
      filteredAddressBoundaries.length > 0 ||
      filteredPlacementBoundaries.length > 0
    ) {
      filteredAddressBoundaries.forEach((b) => dedupedBoundaries.set(b.id, b));
      filteredPlacementBoundaries.forEach((b) =>
        dedupedBoundaries.set(b.id, b)
      );
    } else {
      filteredAllBoundaries.forEach((b) => dedupedBoundaries.set(b.id, b));
    }

    return Array.from(dedupedBoundaries.values());
  }, [addressBoundaries, selectedPlacementBoundaries, allBoundaries]);

  React.useEffect(() => {
    if (config) {
      dispatch(fetchBoundary(config));
    }
  }, [dispatch, config]);

  // WARNING!!! when strict mode is enabled, a selected boundary will stay drawn on the map when it should be removed.
  // the data in 'boundaries' is correct, but the last selected boundary continues to appear on the map.
  return <MapPolygons boundaries={boundaries}></MapPolygons>;
};
